'use client';

import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../utils/cn';
import { useProfileLayout } from '../layouts/profile-layout-context';

export function ModuleEntryCard({
  className,
  contentClassName,
  children,
  ...props
}: ComponentProps<'div'> & { contentClassName?: string }) {
  const layout = useProfileLayout();

  if (layout === 'classic') {
    // Handover .lrow: 16px mobile / 15px desktop vertical padding, hairline rules.
    return (
      <div
        className={cn(
          'border-b border-[var(--border)] last:border-b-0',
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            'py-4 @min-[880px]/profile:py-[15px]',
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>
    );
  }

  if (layout === 'centered') {
    return (
      <div
        className={cn(
          'overflow-hidden rounded-full border border-[var(--border)] bg-[var(--surface)]',
          className,
        )}
        {...props}
      >
        <div className={cn('px-5 py-[15px]', contentClassName)}>{children}</div>
      </div>
    );
  }

  // cards — standalone bordered cards on mobile; inset surface2 rows inside
  // module shell on desktop (handover profil-cards-desktop .row).
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] transition-colors hover:bg-[var(--surface2)]',
        '@min-[880px]/profile:rounded-[13px] @min-[880px]/profile:bg-[var(--surface2)] @min-[880px]/profile:hover:bg-[var(--surface2)]',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'p-[15px] @min-[880px]/profile:p-[13px]',
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function ModuleEntryRow({
  className,
  children,
  ...props
}: ComponentProps<'div'>) {
  const layout = useProfileLayout();

  return (
    <div
      className={cn(
        'flex min-w-0 items-center text-[var(--text)]',
        layout === 'classic'
          ? 'gap-[15px] @min-[880px]/profile:gap-3.5'
          : layout === 'centered'
            ? 'gap-[11px]'
            : 'gap-3.5 @min-[880px]/profile:gap-[13px]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function ModuleEntryIcon({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const layout = useProfileLayout();

  if (layout === 'classic') {
    return (
      <div
        className={cn(
          'flex h-[19px] w-[19px] shrink-0 items-center justify-center text-[var(--accent)] [&_svg]:h-[19px] [&_svg]:w-[19px]',
          className,
        )}
        style={{ color: 'var(--accent)' }}
      >
        {children}
      </div>
    );
  }

  if (layout === 'centered') {
    return (
      <div
        className={cn(
          'flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[var(--accent)] [&_svg]:h-[18px] [&_svg]:w-[18px]',
          className,
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-[var(--accent-12)] text-[var(--accent)] [&_svg]:h-[19px] [&_svg]:w-[19px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ModuleEntryLabelValue({
  label,
  value,
  className,
}: {
  label?: string | null;
  value: string;
  className?: string;
}) {
  const layout = useProfileLayout();

  if (layout === 'centered') {
    return (
      <span
        className={cn(
          'flex min-w-0 flex-1 items-center justify-between gap-3',
          className,
        )}
      >
        {label?.trim() ? (
          <span className="text-[14px] font-semibold text-[var(--text)]">
            {label}
          </span>
        ) : null}
        <span className="truncate text-[12.5px] font-medium text-[var(--muted)]">
          {value}
        </span>
      </span>
    );
  }

  if (label?.trim()) {
    return (
      <span
        className={cn(
          'flex min-w-0 flex-1 flex-col items-start gap-0',
          className,
        )}
      >
        <span
          className={cn(
            'text-[12px] text-[var(--muted)]',
            layout === 'classic' && 'order-2 mt-px text-[var(--faint)]',
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            'mt-0.5 text-[14px] font-semibold text-[var(--text)]',
            layout === 'classic' &&
              'order-1 mt-0 text-[15px] font-medium @min-[880px]/profile:text-[14px]',
          )}
        >
          {value}
        </span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        'min-w-0 flex-1 text-[14px] font-semibold text-[var(--text)]',
        layout === 'classic' &&
          'text-[15px] font-medium @min-[880px]/profile:text-[14px]',
        className,
      )}
    >
      {value}
    </span>
  );
}

export function ModuleEntryTitleBlock({
  title,
  description,
  className,
}: {
  title: string;
  description?: string | null;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0 flex-1', className)}>
      <div className="text-[14px] font-semibold text-[var(--text)]">
        {title}
      </div>
      {description?.trim() ? (
        <div className="mt-0.5 text-[12px] text-[var(--muted)]">
          {description}
        </div>
      ) : null}
    </div>
  );
}
